import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { retailersService } from '../services/retailers.service';
import type {
  RetailerFilters,
  CreateRetailerDTO,
  UpdateRetailerDTO,
  BulkUpdateRetailersDTO,
} from '../types/retailers.types';

export const RETAILERS_QUERY_KEY = 'retailers';

export function useRetailersQuery(filters: RetailerFilters = {}) {
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY, filters],
    queryFn: () => retailersService.list(filters),
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useRetailerQuery(retailerId: string | null) {
  return useQuery({
    queryKey: [RETAILERS_QUERY_KEY, retailerId],
    queryFn: () => retailersService.getById(retailerId!),
    enabled: !!retailerId,
  });
}

export function useCreateRetailerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRetailerDTO) => retailersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}

export function useUpdateRetailerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ retailerId, data }: { retailerId: string; data: UpdateRetailerDTO }) =>
      retailersService.update(retailerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}

export function useDeleteRetailerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (retailerId: string) => retailersService.delete(retailerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}

export function useBulkUpdateRetailersMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateRetailersDTO) => retailersService.bulkUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}

export function useImportExcelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => retailersService.importExcel(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RETAILERS_QUERY_KEY] });
    },
  });
}
